/**
 * Accessory Recommendations Manager
 * Handles showing accessory product recommendations when items are added to cart
 */

class AccessoryRecommendationsManager {
  constructor() {
    this.overlay = null;
    this.enabled = true;
    this.accessoryCollectionHandle = 'zubehor'; // Collection handle for accessories
    this.previousCartItemCount = 0; // Track previous cart item count
    this.previousCartVariantIds = new Set(); // Track variant IDs in cart
    this.debug = false; // Set to true for console logs

    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[AccessoryRec]', ...args);
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    // Get overlay element
    this.overlay = document.querySelector('accessory-recommendations-overlay');

    if (!this.overlay) {
      this.log('Overlay not found in DOM');
      return;
    }

    this.log('Setup complete, overlay found');

    // Initialize cart state
    await this.updateCartState();

    // Listen for cart update events
    this.subscribeToCartEvents();
  }

  subscribeToCartEvents() {
    // Subscribe to cart update events using PubSub
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.log('Subscribing to PubSub cart events');
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.handleCartUpdate(event);
      });
    } else {
      this.log('PubSub not available, using fallback');
    }

    // Listen for fetch requests to cart/add.js
    this.interceptCartAdd();

    // Fallback: Listen for form submissions from product forms only
    document.addEventListener('submit', (e) => {
      // IMPORTANT: Ignore ALL cart/checkout forms
      const formId = e.target.id;
      const formAction = e.target.action;

      // List of form IDs and actions to ignore (cart/checkout forms)
      const isCartForm = formId === 'cart' ||
                        formId === 'CartDrawer-Form' ||
                        formAction?.includes('/cart') ||
                        formAction?.includes('/checkout') ||
                        e.target.querySelector('[name="checkout"]'); // Has checkout button

      if (isCartForm) {
        return; // This is a cart/checkout form - NEVER touch it!
      }

      // Only handle product-form submissions
      const productForm = e.target.closest('product-form');
      if (!productForm) {
        return; // Not a product form, ignore it
      }

      const form = e.target;
      const variantIdInput = form.querySelector('[name="id"]');
      if (variantIdInput) {
        const variantId = variantIdInput.value;
        this.log('Form submit detected, variant:', variantId);
        // Wait a bit for cart to update
        setTimeout(() => {
          this.checkAndShowRecommendations(variantId);
        }, 800);
      }
    });
  }

  // Intercept fetch calls to cart/add.js
  interceptCartAdd() {
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const [url, options] = args;
      
      // Check if this is a cart add request
      if (url && (url.includes('/cart/add') || url.includes('cart/add.js'))) {
        self.log('Intercepted cart/add request');
        
        return originalFetch.apply(this, args).then(async (response) => {
          // Clone response so we can read it
          const clonedResponse = response.clone();
          
          try {
            // Wait a bit then check cart
            setTimeout(async () => {
              self.log('Checking cart after add...');
              const cartResponse = await fetch('/cart.js');
              const cart = await cartResponse.json();
              
              if (cart.items && cart.items.length > 0) {
                // Get the last added item
                const lastItem = cart.items[cart.items.length - 1];
                self.log('Last item in cart:', lastItem.handle);
                
                // Check if we should show recommendations
                if (cart.items.length > self.previousCartItemCount) {
                  self.log('New item detected, showing recommendations');
                  self.checkAndShowRecommendations(lastItem.variant_id);
                }
                
                // Update state
                self.previousCartItemCount = cart.items.length;
                self.previousCartVariantIds = new Set(cart.items.map(item => item.variant_id || item.id));
              }
            }, 1000);
          } catch (e) {
            self.log('Error in cart intercept:', e);
          }
          
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };
    
    this.log('Cart add interceptor installed');
  }

  async handleCartUpdate(event) {
    this.log('Cart update event received:', event);

    // Ignore updates from accessory recommendations overlay itself
    if (event.source === 'accessory-recommendation') {
      // Still update the cart state
      if (event.cartData && event.cartData.items) {
        this.previousCartItemCount = event.cartData.items.length;
        this.previousCartVariantIds = new Set(event.cartData.items.map(item => item.variant_id || item.id));
      }
      return;
    }

    // Get cart data
    const cartData = event.cartData;

    if (!cartData || !cartData.items) {
      this.log('No cart data in event');
      return;
    }

    // Skip if manager is disabled
    if (!this.enabled) {
      this.log('Manager disabled, skipping');
      // Still update state even when disabled
      this.previousCartItemCount = cartData.items.length;
      this.previousCartVariantIds = new Set(cartData.items.map(item => item.variant_id || item.id));
      return;
    }

    // Check if a new item was added (not just quantity changed)
    const currentItemCount = cartData.items.length;
    const currentVariantIds = new Set(cartData.items.map(item => item.variant_id || item.id));

    this.log('Previous items:', this.previousCartItemCount, 'Current:', currentItemCount);

    // Only show recommendations if a NEW variant was added to cart
    if (currentItemCount > this.previousCartItemCount ||
        currentVariantIds.size > this.previousCartVariantIds.size) {

      // Find the newly added variant(s)
      const newVariantIds = [...currentVariantIds].filter(id => !this.previousCartVariantIds.has(id));

      this.log('New variants detected:', newVariantIds);

      if (newVariantIds.length > 0) {
        // Show recommendations for the first new variant
        this.checkAndShowRecommendations(newVariantIds[0]);
      }
    }

    // Update tracked state
    this.previousCartItemCount = currentItemCount;
    this.previousCartVariantIds = currentVariantIds;
  }

  async checkAndShowRecommendations(variantId) {
    if (!this.enabled || !this.overlay) {
      this.log('Cannot show: enabled=', this.enabled, 'overlay=', !!this.overlay);
      return;
    }

    try {
      this.log('Checking recommendations for variant:', variantId);
      
      // Get product handle from variant ID
      const productHandle = await this.getProductHandleFromVariant(variantId);
      
      this.log('Product handle:', productHandle);
      
      if (productHandle) {
        // Show overlay with product ID
        this.overlay.open(productHandle);
      }
    } catch (error) {
      console.error('Error showing accessory recommendations:', error);
    }
  }

  async getProductHandleFromVariant(variantId) {
    try {
      // Try to get from cart first
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find the item with this variant ID
      const item = cart.items.find(item => item.variant_id == variantId);
      
      if (item && item.handle) {
        return item.handle;
      }

      // Fallback: try to get from variant directly (not always available)
      return null;
    } catch (error) {
      console.error('Error getting product handle:', error);
      return null;
    }
  }

  disable() {
    this.enabled = false;
    this.log('Manager disabled');
  }

  enable() {
    this.enabled = true;
    this.log('Manager enabled');
  }

  async updateCartState() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.items) {
        this.previousCartItemCount = cart.items.length;
        this.previousCartVariantIds = new Set(cart.items.map(item => item.variant_id || item.id));
        this.log('Cart state initialized:', this.previousCartItemCount, 'items');
      }
    } catch (error) {
      console.error('Error updating cart state:', error);
    }
  }
  
  // Manual trigger for testing
  async testShow() {
    if (this.overlay) {
      this.overlay.open('test');
    }
  }
}

// Initialize the manager when script loads
if (typeof window !== 'undefined') {
  window.accessoryRecommendationsManager = new AccessoryRecommendationsManager();
}
